// Formato del lote/OP de captura (`registros_produccion_hora.op_lote_oc_cliente`)
// configurable por empresa (B7, propuesta 2026-10-alimentos-rionegro).
//
// La regla vive en `tenants.formato_lote_regex` (migración 097). Los schemas
// compartidos solo validan la FORMA genérica del campo (texto no vacío de
// hasta 32 caracteres); la regla de la empresa la aplica el API con el valor
// del tenant (código `lote_formato_invalido`) y el FE con el mismo valor
// servido por /api/me. El default reproduce la regla histórica: 8 dígitos.
//
// NO aplica al número de OP de `programacion_op` (orden de producción del
// cliente): ese sigue con `opLoteOcRegex` fijo.
import { z } from 'zod';
/** Defaults de la migración 097 (idénticos a la regla anterior al cambio). */
export const FORMATO_LOTE_REGEX_DEFAULT = '^[0-9]{8}$';
export const FORMATO_LOTE_EJEMPLO_DEFAULT = '10349723';
export const FORMATO_LOTE_AYUDA_DEFAULT = '8 dígitos';
/** Longitud máxima del lote (espejo de la columna y del schema de máquina). */
export const LOTE_MAX_LEN = 32;
/** Longitud máxima de la expresión (espejo del CHECK de la migración 097). */
export const FORMATO_LOTE_REGEX_MAX_LEN = 200;
/** Forma genérica del campo lote, independiente del formato de la empresa. */
export const loteCampoSchema = z
    .string()
    .trim()
    .min(1, 'El lote es obligatorio')
    .max(LOTE_MAX_LEN, `Máximo ${LOTE_MAX_LEN} caracteres`);
/**
 * Compila la expresión de la empresa. Devuelve null si no compila (nunca
 * lanza): el caller decide si eso es un error de configuración o un rechazo.
 */
export function compilarFormatoLote(regexSource) {
    if (regexSource.length === 0 || regexSource.length > FORMATO_LOTE_REGEX_MAX_LEN)
        return null;
    try {
        return new RegExp(regexSource);
    }
    catch {
        return null;
    }
}
/**
 * ¿Cumple `valor` (ya recortado) el formato de la empresa? Una expresión que
 * no compila rechaza todo: mejor bloquear la captura que aceptar basura.
 */
export function validarLote(valor, regexSource) {
    if (valor.length === 0 || valor.length > LOTE_MAX_LEN)
        return false;
    const re = compilarFormatoLote(regexSource);
    return re !== null && re.test(valor);
}
/** Mensaje de rechazo para el usuario, con la ayuda de la empresa. */
export function mensajeLoteInvalido(ayuda) {
    return `El lote no cumple el formato de la empresa (${ayuda})`;
}
/** Schema del lote con el formato de una empresa concreta (FE y tests). */
export function loteSchemaPara(regexSource, ayuda = FORMATO_LOTE_AYUDA_DEFAULT) {
    return loteCampoSchema.refine((v) => validarLote(v, regexSource), {
        message: mensajeLoteInvalido(ayuda),
    });
}
/**
 * Los tres campos del formato tal como los edita el Admin Tenant. El refine
 * cruzado (compila + ejemplo cumple) lo aplica `validarFormatoLoteConfig`,
 * porque el PATCH es parcial y el API combina con los valores guardados.
 */
export const formatoLoteRegexSchema = z
    .string()
    .trim()
    .min(1, 'La expresión es obligatoria')
    .max(FORMATO_LOTE_REGEX_MAX_LEN, `Máximo ${FORMATO_LOTE_REGEX_MAX_LEN} caracteres`);
export const formatoLoteEjemploSchema = loteCampoSchema;
export const formatoLoteAyudaSchema = z
    .string()
    .trim()
    .min(1, 'La ayuda es obligatoria')
    .max(80, 'Máximo 80 caracteres');
/** null = configuración coherente; string = motivo del rechazo (UI en español). */
export function validarFormatoLoteConfig(regexSource, ejemplo) {
    if (compilarFormatoLote(regexSource) === null) {
        return 'La expresión del formato de lote no es válida';
    }
    if (!validarLote(ejemplo.trim(), regexSource)) {
        return 'El ejemplo no cumple la expresión del formato de lote';
    }
    return null;
}
//# sourceMappingURL=formato_lote.js.map